import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import TopBar from "../../../../components/TopBar/TopBar";
import { getClinicPaymentMethodsWoLoader } from "../../../../redux/reducers/clinicSlice";
import {
  createPaymentMethod,
  deletePaymentMethod,
  getPaymentMethods,
  updatePaymentMethod,
} from "../../../../redux/reducers/paymentMethodSlice";
import { AppColors } from "../../../../util/AppColors";
import { billAsForBillingHistory } from "../../../../util/dropList";
import { patchMandateSession } from "../../../../util/mandateSessionStorage";
import { tblGrayBack } from "../../../../util/object";
import { canWriteModule } from "../../../../util/roleModuleAccess";

const toPaymentMethodList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

function PaymentMethod() {
  const dispatch = useDispatch();
  const canManageBilling = canWriteModule("billing");
  const [search, setSearch] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState({ name: "", status: true, type: [] });
  const [delModVisible, setDelModVisible] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const paymentState = useSelector((state) => state.paymentMethod);
  const rows = Array.isArray(paymentState?.list) ? paymentState.list : [];

  useEffect(() => {
    dispatch(getPaymentMethods());
  }, [dispatch]);

  const refreshPaymentMethodsAndPatchMandate = () => {
    try {
      dispatch(getPaymentMethods());
      dispatch(getClinicPaymentMethodsWoLoader()).then((res) => {
        try {
          const payload = res?.payload;
          if (payload == null) return;
          patchMandateSession({
            paymentMethods: toPaymentMethodList(payload),
          });
        } catch {
          // ignore mandate patch errors
        }
      });
    } catch {
      // ignore refresh errors
    }
  };

  const handleAddOrUpdate = () => {
    if (!form.name.trim()) {
      alert("Please enter a payment method name.");
      return;
    }
    const userInput = form.name.trim();
    if (!userInput || userInput.length === 0) return;

    const typeValues = Array.isArray(form?.type)
      ? form.type.map((li) => li?.value).filter(Boolean)
      : [];

    if (editIndex !== null) {
      const method = rows[editIndex];
      if (!method?.methodId) return;
      dispatch(
        updatePaymentMethod({
          methodId: method.methodId,
          name: userInput,
          status: form?.status,
          type: typeValues,
        }),
      ).then((res) => {
        if (res?.payload) {
          refreshPaymentMethodsAndPatchMandate();
          handleDrawerClose();
        }
      });
      return;
    }

    dispatch(
      createPaymentMethod({
        name: form?.name,
        status: form?.status,
        type: typeValues,
      }),
    ).then((res) => {
      if (res?.payload) {
        refreshPaymentMethodsAndPatchMandate();
        handleDrawerClose();
      }
    });
  };

  const handleEdit = (index) => {
    const method = rows[index];
    if (!method) return;

    setForm({
      name: method.name || "",
      status: method.status,
      type:
        Array.isArray(method?.type) && method.type.length > 0
          ? method.type.map((ty) => ({ name: ty, value: ty }))
          : [],
    });
    setEditIndex(index);
    setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setForm({ name: "", status: true, type: [] });
    setEditIndex(null);
  };

  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setDelModVisible(true);
  };

  const handleDelConfirm = () => {
    const method = rows[deleteIndex];
    if (!method?.methodId) {
      setDelModVisible(false);
      setDeleteIndex(null);
      return;
    }
    dispatch(deletePaymentMethod({ methodId: method.methodId })).then((res) => {
      if (res?.payload) {
        refreshPaymentMethodsAndPatchMandate();
      }
      setDelModVisible(false);
      setDeleteIndex(null);
    });
  };

  const handleDelModClose = () => {
    setDelModVisible(false);
    setDeleteIndex(null);
  };

  const filteredRows = rows.filter((row) =>
    (row?.name || "").toLowerCase().includes((search || "").toLowerCase()),
  );

  return (
    <Box>
      <TopBar sx={{ py: 1, minHeight: "unset" }}>
        <Container maxWidth="xl">
          <div className="flex-row">
            <div className="top-row-cen-con w100Per">
              <div className="w30Per">
                <CustomTextField
                  placeholder="Search"
                  value={search}
                  handleChange={(e) => setSearch(e.target.value)}
                  backgroundColor={AppColors.lightBlue2}
                  fullWidth
                />
              </div>
              {canManageBilling ? (
                <div className="ml20">
                  <CustomButton
                    text="Create"
                    onClick={() => setOpenDrawer(true)}
                    smallBtn
                  />
                </div>
              ) : null}
            </div>
          </div>
          <Typography className="font-medium fs12 gray28 mt5">
            Total Payment Methods: {filteredRows.length}
          </Typography>
        </Container>
      </TopBar>

      {/* Table */}
      <Box className="ph20">
        <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  "S.No",
                  "Payment Method",
                  "Status",
                  "User Name",
                  "Created Date",
                  ...(canManageBilling ? ["Actions"] : []),
                ].map((li, i) => (
                  <TableCell
                    style={tblGrayBack}
                    className="txt-semi-bold fs12"
                    key={i + li}
                  >
                    {li}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody sx={{ overflowY: "auto" }}>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, index) => (
                  <TableRow key={row.methodId}>
                    <TableCell>{index + 1}</TableCell>
                    {/* <TableCell>{row.methodId}</TableCell> */}
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.status ? "Active" : "Inactive"}</TableCell>
                    {/* <TableCell>{row.createdBy || '-'}</TableCell> */}
                    <TableCell>{row.userName || "-"}</TableCell>
                    <TableCell>
                      {row.createdDate
                        ? new Date(row.createdDate).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    {canManageBilling ? (
                      <TableCell>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEdit(index)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteClick(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Drawer for Create/Edit */}
      <Drawer anchor="right" open={openDrawer} onClose={handleDrawerClose}>
        <Box p={3} width={300}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" color="primary">
              {editIndex !== null
                ? "Edit Payment Method"
                : "Create Payment Method"}
            </Typography>
            <IconButton onClick={handleDrawerClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            label="Payment Method"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            margin="normal"
            InputLabelProps={{
              sx: {
                position: "absolute",
                top: "50%",
                left: "14px",
                transform: "translateY(-50%)",
                transition: "all 0.2s ease-out",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                  top: "0px",
                  transform: "translateY(-50%) scale(0.75)",
                  transformOrigin: "top left",
                },
              },
            }}
          />
          <div className="mt10">
            <CustomSelect
              list={billAsForBillingHistory}
              handleChange={(e) =>
                setForm({ ...form, type: e?.target?.value ?? [] })
              }
              name="type"
              label="Type"
              multiSelectSearchCheck
              value={form.type ?? []}
            />
          </div>
          <TextField
            fullWidth
            select
            label="Status"
            value={form.status ? "true" : "false"}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value === "true" })
            }
            margin="normal"
            SelectProps={{ native: true }}
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </TextField>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleAddOrUpdate}
          >
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </Box>
      </Drawer>

      {/* Delete Confirmation Modal */}
      <CustomModal
        open={delModVisible}
        onClose={handleDelModClose}
        header="Delete"
        modal
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          Are you sure you want to delete this record?
        </Typography>
        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton text="Cancel" onClick={handleDelModClose} smallBtn />
          </div>
          <div className="ml10">
            <CustomButton
              redBtn
              text="Confirm"
              onClick={handleDelConfirm}
              smallBtn
            />
          </div>
        </div>
      </CustomModal>
    </Box>
  );
}

export default PaymentMethod;
